import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JoyrideService } from 'ngx-joyride';
import notify from 'devextreme/ui/notify';
import { CataloguePlanningService } from './catalogue-planning.service';

export interface JourneyPhase {
  id: string;
  route: string;
  steps: string[];
}

const JOURNEY_STORAGE_KEY = 'promoflo_journey_phase_index';

/** Phases that route to pages catalogue tenants don't use (their sidebar hides these pages). */
const CATALOGUE_SKIPPED_PHASES: ReadonlySet<string> = new Set([
  'settings-slot-types', 'settings-media-types', 'settings-store-positions', 'slot-products', 'media-planning'
]);

/**
 * Marks that the journey has already been offered to whoever uses this browser.
 *
 * Deliberately localStorage rather than sessionStorage: the phase index above is
 * per-session state ("where am I up to right now"), this is a durable decision
 * ("don't offer it again"). It is per-browser, not per-user — an existing user on a
 * new machine, a cleared cache or a shared workstation will all see the journey
 * again. That is a known limitation of this first pass; the durable answer is a
 * nullable `promoflo_tour_seen_at` on the authentication-api `users` table, backfilled
 * to NOW() for existing rows so NULL only ever means "created after this shipped",
 * read from the same user-details payload that already carries `theme`.
 */
const JOURNEY_SEEN_KEY = 'promoflo_journey_seen';

/**
 * Drives the end-to-end onboarding journey: settings setup -> slot products ->
 * create a promotion -> view/create media plan -> export media plan. Each phase is a
 * single-page joyride tour (see the matching joyrideStep directives in each page's
 * template); this service just tracks which phase is active and auto-navigates to the
 * next page once a phase's tour finishes.
 *
 * It starts itself once per browser for a user who has never seen it, and can be
 * replayed on demand from the "Take the tour" item in the profile dropdown.
 */
@Injectable({ providedIn: 'root' })
export class JourneyService {

  private readonly allPhases: JourneyPhase[] = [
    { id: 'settings-slot-types', route: '/slotting-types', steps: ['settings-add'] },
    { id: 'settings-buying-groups', route: '/buying-groups', steps: ['settings-add'] },
    { id: 'settings-media-types', route: '/media-types', steps: ['settings-add'] },
    { id: 'settings-store-positions', route: '/store-positons', steps: ['settings-add'] },
    { id: 'settings-warehouses', route: '/warehouses', steps: ['settings-add'] },
    { id: 'slot-products', route: '/slotting-board-v2', steps: ['slot-cell', 'slot-create-promotion'] },
    { id: 'find-promotion', route: '/promotions', steps: ['promo-row-actions'] },
    { id: 'media-planning', route: '/media-planning', steps: ['media-plan-edit', 'media-plan-export'] },
  ];

  /**
   * The phases that apply to this customer.
   *
   * Only the V2 slotting board carries the slot-cell / slot-create-promotion joyrideStep
   * directives, so customers on the original board skip that phase rather than being
   * force-navigated to a board they don't use. Skipping matters more than it looks:
   * joyride retries a step that isn't in the DOM forever (JoyrideStepDoesNotExist
   * re-enters tryShowStep), so landing the tour on the original board would hang it
   * rather than fail visibly.
   *
   * The V2 board picks weekly vs monthly from the same config itself, so the route below
   * doesn't need to distinguish them.
   */
  private get phases(): JourneyPhase[] {
    if (this.cataloguePlanningEnabled()) {
      return this.allPhases.filter(phase => !CATALOGUE_SKIPPED_PHASES.has(phase.id));
    }
    return this.slottingBoardV2Enabled()
      ? this.allPhases
      : this.allPhases.filter(phase => phase.id !== 'slot-products');
  }

  /**
   * Catalogue tenants don't slot products (/slotting-board-v2 redirects them), don't use
   * media planning and don't see the slotting-only settings, so those phases are skipped. Reads the cached method; until it is known,
   * or if it couldn't be read, the tenant is treated as slotting.
   */
  private cataloguePlanningEnabled(): boolean {
    return this.cataloguePlanning.methodState$.value?.method === 'catalogue';
  }

  /**
   * `slottingType` config: '2' = V2 weekly, '3' = V2 monthly, anything else (including
   * missing) = the original board at /slotting. Read from the ConfigData that
   * StartupService caches in sessionStorage — the same source the promotion header reads
   * its flags from — so this stays synchronous for the navigation path above.
   */
  private slottingBoardV2Enabled(): boolean {
    try {
      const raw = JSON.parse(sessionStorage.getItem('ConfigData') || '[]');
      const configData: any[] = Array.isArray(raw) ? raw : Object.values(raw ?? {});
      const slottingType = configData.find((config: any) => config?.config_key === 'slottingType');
      const value = (slottingType?.config_string ?? '').toString().trim();
      return value === '2' || value === '3';
    } catch {
      return false;
    }
  }

  private currentIndex: number = -1;

  /** Set by the dismissal listeners below while a phase's tour is on screen. */
  private dismissed: boolean = false;

  constructor(private joyrideService: JoyrideService, private router: Router,
    private cataloguePlanning: CataloguePlanningService) {
    const raw = sessionStorage.getItem(JOURNEY_STORAGE_KEY);
    this.currentIndex = raw != null ? JSON.parse(raw) : -1;
  }

  get isActive(): boolean {
    return this.currentIndex >= 0 && this.currentIndex < this.phases.length;
  }

  get currentPhase(): JourneyPhase | null {
    return this.isActive ? this.phases[this.currentIndex] : null;
  }

  /** True once the journey has been shown to whoever uses this browser. */
  hasSeen(): boolean {
    return localStorage.getItem(JOURNEY_SEEN_KEY) === 'true';
  }

  /**
   * Starts the journey for a user who has never been shown it. Safe to call on every
   * app load — no-ops for anyone who has already seen, finished or dismissed it.
   */
  startIfNotSeen(): void {
    if (this.hasSeen() || this.isActive || this.joyrideService.isTourInProgress()) {
      return;
    }
    this.start();
  }

  /** Begins the journey from the first phase, navigating to its page. */
  start(): void {
    this.currentIndex = 0;
    this.persist();
    this.router.navigateByUrl(this.phases[0].route);
  }

  /** Ends the journey early (e.g. the user navigates away or closes the tour). */
  stop(): void {
    this.currentIndex = -1;
    this.persist();
    if (this.joyrideService.isTourInProgress()) {
      this.joyrideService.closeTour();
    }
  }

  /**
   * Called by a page once it's ready to be toured. No-ops unless the journey is active
   * and this page is the current phase, so it's always safe to call on every page load.
   */
  tryStartPhase(phaseId: string): void {
    const phase = this.currentPhase;
    if (!phase || phase.id !== phaseId || this.joyrideService.isTourInProgress()) {
      return;
    }
    this.dismissed = false;
    this.listenForDismissal();
    const startedIndex = this.currentIndex;
    this.joyrideService.startTour({
      steps: phase.steps,
      showPrevButton: true,
      themeColor: '#2196F3',
      waitingTime: 250
    }).subscribe({
      complete: () => {
        this.stopListeningForDismissal();
        // completePhase() already moved the journey on while this tour was showing.
        if (this.currentIndex !== startedIndex) return;
        // Reaching the end of a phase or bailing out both count as having been shown
        // the journey, so neither will auto-start it again.
        this.markSeen();
        if (this.dismissed) {
          this.stop();
        } else {
          this.advance();
        }
      }
    });
  }

  /**
   * Marks a phase as done because the user did what it asks, e.g. opened "Plan Catalogue"
   * during find-promotion. No-ops unless it is the current phase. The user's own navigation
   * wins: this never navigates, it only moves the journey on (or ends it, if it was the last
   * phase).
   */
  completePhase(phaseId: string): void {
    if (this.currentPhase?.id !== phaseId) {
      return;
    }
    this.stopListeningForDismissal();
    this.markSeen();
    this.advance(false);
    if (this.joyrideService.isTourInProgress()) {
      this.joyrideService.closeTour();
    }
  }

  private advance(navigate: boolean = true): void {
    if (!this.isActive) {
      return;
    }
    this.currentIndex++;
    this.persist();
    if (this.currentIndex < this.phases.length) {
      if (navigate) this.router.navigateByUrl(this.phases[this.currentIndex].route);
    } else {
      this.currentIndex = -1;
      this.persist();
      // Finishing the last phase leaves the user on the media plan. Drop them back to
      // the dashboard so the journey ends where it started. Skipping deliberately does
      // not, so bailing out leaves you on the page you were looking at - see stop().
      if (navigate) this.router.navigateByUrl('/');
      notify({ message: 'Journey complete — you\'ve covered the full promotion workflow.' }, 'success', 4000);
    }
  }

  /*
   * ngx-joyride ends a tour identically whether the user finished it or bailed out:
   * JoyrideStepComponent's close button, its Done button and the Escape key all call
   * JoyrideStepService.close(), which completes the same observable. `complete` alone
   * therefore can't tell the two apart — and without that distinction a dismissal falls
   * through to advance(), which just pushes the user to the next phase's page. Across
   * eight phases that means the close button never actually escapes the journey.
   *
   * So watch for the two dismissal gestures directly. Anything else that ends the tour
   * (the Done button, or Right Arrow on the last step) leaves `dismissed` false and is
   * treated as finishing the phase.
   */

  private listenForDismissal(): void {
    document.addEventListener('click', this.onDismissClick, true);
    document.addEventListener('keyup', this.onDismissKey, true);
  }

  private stopListeningForDismissal(): void {
    document.removeEventListener('click', this.onDismissClick, true);
    document.removeEventListener('keyup', this.onDismissKey, true);
  }

  /** Capture-phase so the step is still in the DOM when we test the click target. */
  private readonly onDismissClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('.joyride-step__close')) {
      this.dismissed = true;
    }
  };

  private readonly onDismissKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.dismissed = true;
    }
  };

  private markSeen(): void {
    localStorage.setItem(JOURNEY_SEEN_KEY, 'true');
  }

  private persist(): void {
    sessionStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(this.currentIndex));
  }
}
